/**
 * SERVICEABILITY & MICROWAVE-LINK SOFT-FAIL (docs/engineering/25_serviceability_and_link_budget.md)
 *
 * A tower can be structurally sound yet still "fail" in service: it deflects,
 * sways and twists enough that a microwave link drops. This is a *soft-fail* —
 * an outage, not a collapse — and it bites FIRST on the smallest, highest-freq
 * dishes, because a narrow beam has no pointing tolerance to spare.
 *
 * This module is READ-ONLY with respect to the solve: it observes node positions
 * each frame and derives serviceability quantities. Nothing here moves a node or
 * fails a member. It mirrors the `maxFoundationUtil` getter pattern on Solver.
 *
 * Quantities computed (all SI; angles in radians internally, exposed in degrees):
 *  - top horizontal deflection as a fraction of tower height (TIA-222 limit
 *    ~2–3% of H at service wind — doc 25 §1, doc 13 §5).
 *  - per-appurtenance TILT (local centreline slope, the "sway") — doc 25 §6.
 *  - per-appurtenance TWIST (rotation of the tower about its own vertical axis
 *    at the mount height) — doc 25 §6.
 *  - for microwave DISHES: the half-power beamwidth θ₃dB ≈ 70·λ/D (doc 25 §2 eq 1),
 *    the allowable pointing error θ_allow (doc 25 §4 eq 3, at the datasheet's
 *    survival budget = 0.3·θ₃dB), the current pointing error θ_err (eq 4), the
 *    pointing loss L_point (eq 2) and a LINK STATUS (ok / degraded / lost).
 *
 * Twist couples into pointing through the mount stand-off boom arm (doc 25 §6):
 * a dish slung far off the mast swings its boresight by ~θ_twist·(arm/aim-lever),
 * whereas a flush dish barely re-aims. We use the mount `standoff` as that lever.
 */
import type { Structure } from "@/physics/model";
import type { MountedLoad } from "@/game/appurtenances";

/** Speed of light (m/s) — for λ = c/f. */
const C_LIGHT = 3e8;
/** Parabolic half-power-beamwidth constant (deg): θ₃dB ≈ 70·λ/D (doc 25 §2). */
const HPBW_CONST = 70;
/**
 * Fraction of the 3 dB beamwidth allowed as pointing error at the SURVIVAL
 * limit — the CommScope VHLP2 datasheet defines survival as 0.3·θ₃dB (doc 25
 * §4). Equivalent to spending M_point = 12·0.3² ≈ 1.08 dB of fade margin.
 */
const SURVIVAL_RATIO = 0.3;
/**
 * Fraction of the 3 dB beamwidth at the OPERATIONAL limit — VHLP2 datasheet
 * "operational wind" = 0.1·θ₃dB (≈0.12 dB) (doc 25 §4). Between operational and
 * survival the link is DEGRADED; past survival it is LOST.
 */
const OPERATIONAL_RATIO = 0.1;
/**
 * A dish whose beam is wider than this (deg) is not serviceability-critical: a
 * ~65° sector panel would need the tower to fall over before a link cared, so we
 * exempt anything this wide (doc 25 §2/§4). Well above any real microwave dish.
 */
const PANEL_EXEMPT_HPBW_DEG = 30;

/** Link states in worsening order. */
export type LinkStatus = "ok" | "degraded" | "lost";

export interface DishServiceability {
  /** Height of the mount above base (m). */
  height: number;
  /** Dish diameter (m) and frequency (GHz) that set the beamwidth. */
  diameter: number;
  freqGhz: number;
  /** Half-power beamwidth θ₃dB (deg) — doc 25 eq (1). */
  hpbwDeg: number;
  /** Allowable pointing error at survival (deg) — doc 25 eq (3) @ 0.3·θ₃dB. */
  allowDeg: number;
  /** Current tilt (local centreline slope) at the mount (deg). */
  tiltDeg: number;
  /** Current twist (rotation about vertical) at the mount (deg). */
  twistDeg: number;
  /** Total pointing error θ_err = tilt + twist·(boom lever) (deg) — doc 25 eq (4). */
  pointingErrDeg: number;
  /** Pointing loss L_point = 12·(θ_err/θ₃dB)² (dB) — doc 25 eq (2). */
  pointingLossDb: number;
  status: LinkStatus;
}

export interface ServiceabilityReport {
  /** Top horizontal deflection as a fraction of tower height (0..). */
  topDriftFrac: number;
  /** Worst (largest) tilt at any mount node (deg). */
  maxTiltDeg: number;
  /** Worst twist at any mount node (deg). */
  maxTwistDeg: number;
  /** Per-dish link serviceability, worst (most-degraded) first. */
  dishes: DishServiceability[];
  /** Worst link status across all dishes ("ok" if there are no dishes). */
  worstLink: LinkStatus;
}

/**
 * A frozen snapshot of the reference (undeformed, as-built) geometry the solver
 * needs to measure deflection/twist against. Captured once, before the tower
 * moves. `rings` are node-index rows bottom→top (the render centreline rings).
 */
export interface ServiceabilityRef {
  rings: number[][];
  /** Reference y of each ring (m). */
  ringY: number[];
  /** Reference centroid (x,z) of each ring — the undeformed centreline. */
  ringCx: number[];
  ringCz: number[];
  /** Reference azimuth of each node about its ring centroid (rad). */
  nodeAz: Float64Array;
  /** Overall tower height (m) for the drift-% denominator. */
  height: number;
  /** Node indices carrying microwave-dish mounted loads. */
  dishMounts: Array<{ nodeIdx: number; load: MountedLoad }>;
}

/** Build the reference snapshot from a freshly-compiled (undeformed) structure. */
export function buildServiceabilityRef(
  structure: Structure,
  rings: number[][],
  height: number,
): ServiceabilityRef {
  const ringY: number[] = [];
  const ringCx: number[] = [];
  const ringCz: number[] = [];
  const nodeAz = new Float64Array(structure.nodes.length);
  for (const row of rings) {
    let cx = 0;
    let cz = 0;
    let y = 0;
    for (const idx of row) {
      const n = structure.nodes[idx]!;
      cx += n.x;
      cz += n.z;
      y += n.y;
    }
    const inv = row.length > 0 ? 1 / row.length : 0;
    cx *= inv;
    cz *= inv;
    y *= inv;
    ringY.push(y);
    ringCx.push(cx);
    ringCz.push(cz);
    for (const idx of row) {
      const n = structure.nodes[idx]!;
      nodeAz[idx] = Math.atan2(n.z - cz, n.x - cx);
    }
  }

  // Gather dish mounts (microwave dishes only — panels/omni are not link-critical).
  const dishMounts: Array<{ nodeIdx: number; load: MountedLoad }> = [];
  for (let i = 0; i < structure.nodes.length; i++) {
    const ml = structure.nodes[i]!.mountedLoads;
    if (!ml) continue;
    for (const { load } of ml) {
      if (load.appurtenance.kind === "dish") dishMounts.push({ nodeIdx: i, load });
    }
  }

  return { rings, ringY, ringCx, ringCz, nodeAz, height, dishMounts };
}

/** Half-power beamwidth θ₃dB (deg) for a dish — doc 25 eq (1). */
export function beamwidthDeg(diameter: number, freqGhz: number): number {
  const lambda = C_LIGHT / (freqGhz * 1e9); // m
  return (HPBW_CONST * lambda) / Math.max(1e-6, diameter);
}

/**
 * Estimate a dish's operating frequency (GHz) from its diameter when the load
 * doesn't carry one. Real deployments pair small dishes with high bands and big
 * dishes with low bands (doc 25 §2 worked table: 0.6 m→11 GHz, 2.4 m→6 GHz), so
 * we interpolate across that plausible range. This keeps the mechanic honest
 * (beamwidth ∝ D·f) without requiring every design to specify a frequency.
 */
function inferFreqGhz(diameter: number): number {
  // 0.3 m → ~23 GHz (small high-band), 3.0 m → ~6 GHz (large backhaul).
  if (diameter <= 0.3) return 23;
  if (diameter >= 3.0) return 6;
  // log-ish taper between the two anchor points.
  const t = (diameter - 0.3) / (3.0 - 0.3);
  return 23 + t * (6 - 23);
}

/**
 * Compute the current serviceability report from live node positions against the
 * reference snapshot. Pure/read-only — never mutates the structure.
 *
 * `solarTiltDeg` (optional, default 0) adds the daily solar-bowing pointing wander
 * (doc 25 §5 eq 4 / doc 14 §5) into θ_err so a link can be tipped over the edge by
 * the afternoon sun even without a storm. Off by default → no change to behaviour.
 */
export function computeServiceability(
  structure: Structure,
  ref: ServiceabilityRef,
  solarTiltDeg = 0,
): ServiceabilityReport {
  const nodes = structure.nodes;
  const nLev = ref.rings.length;

  // --- current centreline (ring centroids) + top drift ---------------------
  const curCx = new Array<number>(nLev);
  const curCz = new Array<number>(nLev);
  for (let l = 0; l < nLev; l++) {
    const row = ref.rings[l]!;
    let cx = 0;
    let cz = 0;
    for (const idx of row) {
      cx += nodes[idx]!.x;
      cz += nodes[idx]!.z;
    }
    const inv = row.length > 0 ? 1 / row.length : 0;
    curCx[l] = cx * inv;
    curCz[l] = cz * inv;
  }
  // Top drift: horizontal move of the top ring centroid vs its reference.
  const top = nLev - 1;
  const topDrift = Math.hypot(curCx[top]! - ref.ringCx[top]!, curCz[top]! - ref.ringCz[top]!);
  const topDriftFrac = ref.height > 0 ? topDrift / ref.height : 0;

  // --- per-level twist (mean node-azimuth rotation about the ring centroid) --
  // averaging cos/sin of the per-node azimuth delta avoids the ±π wrap problem.
  const twistAt = new Array<number>(nLev);
  for (let l = 0; l < nLev; l++) {
    const row = ref.rings[l]!;
    if (row.length < 2) {
      twistAt[l] = 0;
      continue;
    }
    let sSum = 0;
    let cSum = 0;
    for (const idx of row) {
      const n = nodes[idx]!;
      const az = Math.atan2(n.z - curCz[l]!, n.x - curCx[l]!);
      const d = az - ref.nodeAz[idx]!;
      sSum += Math.sin(d);
      cSum += Math.cos(d);
    }
    twistAt[l] = Math.atan2(sSum, cSum); // rad
  }

  // --- per-level tilt (local centreline slope vs reference) -----------------
  // The centreline bends: tilt at level l is the change in horizontal offset
  // between the rings straddling l, divided by their vertical spacing. This is
  // the local boresight rotation that a mast-mounted dish sees (doc 25 §6).
  const tiltAt = new Array<number>(nLev);
  for (let l = 0; l < nLev; l++) {
    const lo = Math.max(0, l - 1);
    const hi = Math.min(nLev - 1, l + 1);
    const dy = ref.ringY[hi]! - ref.ringY[lo]!;
    if (dy <= 1e-6) {
      tiltAt[l] = 0;
      continue;
    }
    // horizontal drift (current − reference) at hi and lo; slope of the drift.
    const dxHi = curCx[hi]! - ref.ringCx[hi]!;
    const dzHi = curCz[hi]! - ref.ringCz[hi]!;
    const dxLo = curCx[lo]! - ref.ringCx[lo]!;
    const dzLo = curCz[lo]! - ref.ringCz[lo]!;
    const slope = Math.hypot(dxHi - dxLo, dzHi - dzLo) / dy;
    tiltAt[l] = Math.atan(slope); // rad
  }

  const RAD2DEG = 180 / Math.PI;
  let maxTiltDeg = 0;
  let maxTwistDeg = 0;
  for (let l = 0; l < nLev; l++) {
    maxTiltDeg = Math.max(maxTiltDeg, Math.abs(tiltAt[l]!) * RAD2DEG);
    maxTwistDeg = Math.max(maxTwistDeg, Math.abs(twistAt[l]!) * RAD2DEG);
  }

  // nearest reference ring level to a mount height.
  const levelAtHeight = (h: number): number => {
    let best = 0;
    let bestD = Infinity;
    for (let l = 0; l < nLev; l++) {
      const d = Math.abs(ref.ringY[l]! - h);
      if (d < bestD) {
        bestD = d;
        best = l;
      }
    }
    return best;
  };

  // --- per-dish link budget --------------------------------------------------
  const dishes: DishServiceability[] = [];
  let worst: LinkStatus = "ok";
  for (const { load } of ref.dishMounts) {
    const diameter = load.appurtenance.diameter ?? 0.6;
    const freqGhz = inferFreqGhz(diameter);
    const hpbwDeg = beamwidthDeg(diameter, freqGhz);
    if (hpbwDeg > PANEL_EXEMPT_HPBW_DEG) continue; // not link-critical

    const lvl = levelAtHeight(load.height);
    const tiltDeg = Math.abs(tiltAt[lvl]!) * RAD2DEG;
    const twistDeg = Math.abs(twistAt[lvl]!) * RAD2DEG;

    // Twist→pointing lever: a dish on a stand-off boom of arm `standoff` swings
    // its boresight by roughly the full twist; a flush dish (small arm) barely
    // re-aims. Normalise by a 1 m reference arm and cap at 1 so a big boom
    // contributes the twist ~1:1 but a tight mount discounts it (doc 25 §6).
    const arm = load.standoff ?? load.appurtenance.standoff ?? 0;
    const twistLever = Math.min(1, arm / 1.0);

    // Total angular error (doc 25 eq 4): wind tilt + boom-scaled twist + solar.
    const pointingErrDeg = tiltDeg + twistDeg * twistLever + solarTiltDeg;

    // Pointing loss (doc 25 eq 2) and allowable (doc 25 eq 3 @ survival budget).
    const pointingLossDb = 12 * Math.pow(pointingErrDeg / hpbwDeg, 2);
    const allowDeg = hpbwDeg * SURVIVAL_RATIO;
    const opDeg = hpbwDeg * OPERATIONAL_RATIO;

    let status: LinkStatus = "ok";
    if (pointingErrDeg >= allowDeg) status = "lost";
    else if (pointingErrDeg >= opDeg) status = "degraded";

    if (status === "lost") worst = "lost";
    else if (status === "degraded" && worst !== "lost") worst = "degraded";

    dishes.push({
      height: load.height,
      diameter,
      freqGhz,
      hpbwDeg,
      allowDeg,
      tiltDeg,
      twistDeg,
      pointingErrDeg,
      pointingLossDb,
      status,
    });
  }

  // worst (most-degraded, then largest loss) first for readouts.
  const order: Record<LinkStatus, number> = { lost: 0, degraded: 1, ok: 2 };
  dishes.sort((a, b) => order[a.status] - order[b.status] || b.pointingLossDb - a.pointingLossDb);

  return { topDriftFrac, maxTiltDeg, maxTwistDeg, dishes, worstLink: worst };
}
